import { TourBagDetail } from '@/api/models';
import { IPutterClub } from './Putter';
import { IPutterDynamicDropdownData, IPutterStaticDropdownData } from './useEditPutter';
import { IActivePlayer } from '@/db/repositories/ActivePlayerRepo';
import { ILieOptionMap, ILoftOptionMap } from '@/library/LoftLieHelper';
import { ShaftLengthOptionMap } from '@/library/ShaftLengthHelper';
import { ISwingWtOptionMap } from '@/library/SwingWeightHelper';
import { isIOSWebView } from '@/utils';
import { handFullNameToAbbreviation } from '@/library/HandHelper';

interface IBuildPutterPayloadProps {
  putterClub: IPutterClub;
  loftDropdownData: ILoftOptionMap | undefined;
  lieDropdownData: ILieOptionMap | undefined;
  putterStaticDropdownData: IPutterStaticDropdownData | undefined;
  putterDynamicDropdownData: IPutterDynamicDropdownData | undefined;
  shaftLengthDropdownData: ShaftLengthOptionMap | undefined;
  swingWtDropdownData: ISwingWtOptionMap | undefined;
}

export const buildPutterPayload = ({
  putterClub,
  putterStaticDropdownData,
  putterDynamicDropdownData,
  lieDropdownData,
  loftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
}: IBuildPutterPayloadProps): Partial<TourBagDetail> => {
  // fill non-id raw text values - ie putterLevelName - since its part of the expected payload
  const lieData = lieDropdownData?.map[putterClub.lie];
  const loftData = loftDropdownData?.map[putterClub.loft];
  const swingWtData = swingWtDropdownData?.map[putterClub.swingWt];
  const shaftLengthData = shaftLengthDropdownData?.map[putterClub.putterShaftLength];
  return {
    // putter props
    isActive: putterClub.isActive,
    hand: handFullNameToAbbreviation(putterClub.putterHand),
    putterLevelId: putterClub.putterLevelId,
    putterLevelName: putterStaticDropdownData?.putterLevel.map[putterClub.putterLevelId]?.name || '',
    putterTypeId: putterClub.putterTypeId,
    putterTypeName: putterStaticDropdownData?.putterType.map[putterClub.putterTypeId]?.name || '',
    headModelId: putterClub.putterHeadModelId,
    headModelName: putterDynamicDropdownData?.putterHeadModel.map[putterClub.putterHeadModelId]?.name || '',
    headFinishId: putterClub.putterHeadFinishId,
    headFinishName: putterDynamicDropdownData?.putterHeadFinish.map[putterClub.putterHeadFinishId]?.name || '',
    putterSightlineId: putterClub.putterSightlineId,
    putterSightlineName: putterDynamicDropdownData?.putterSightline.map[putterClub.putterSightlineId]?.name || '',
    putterFaceId: putterClub.putterFaceId,
    putterFaceName: putterDynamicDropdownData?.putterFace.map[putterClub.putterFaceId]?.name || '',
    putterNeckId: putterClub.putterNeckId,
    putterNeckName: putterDynamicDropdownData?.putterNeck.map[putterClub.putterNeckId]?.name || '',
    putterWeightId: Number(putterClub.soleWtId),
    putterWeightName: putterStaticDropdownData?.putterWeight.map[putterClub.soleWtId]?.name || '',
    putterFinalHW: putterClub.putterFinalHW,
    stamping: putterClub.stamping,
    paintingNotes: putterClub.painting,
    putterShaftId: putterClub.putterShaftId,
    putterShaftName: putterDynamicDropdownData?.putterShaft.map[putterClub.putterShaftId]?.name || '',
    isShaftLengthFinished: putterClub.measured,
    putterGripId: putterClub.putterGripId,
    putterGripName: putterDynamicDropdownData?.putterGrip.map[putterClub.putterGripId]?.name || '',
    tape: putterClub.putterGripTape,
    notes: putterClub.notes,
    itemNo: putterClub.itemNo,
    // loft & lie
    lieDigit: lieData?.lieDigit || '',
    lieDigitId: lieData?.joinTableDigitId,
    lieDigitId1: lieData?.sourceTableDigitId,
    lieFraction: lieData?.lieFraction || '',
    lieFractionId: lieData?.joinTableFractionId,
    lieFractionId1: lieData?.sourceTableFractionId,
    loftDigit: loftData?.loftDigit || '',
    loftDigitId: loftData?.joinTableDigitId,
    loftDigitId1: loftData?.sourceTableDigitId,
    loftFraction: loftData?.loftFraction || '',
    loftFractionId: loftData?.joinTableFractionId,
    loftFractionId1: loftData?.sourceTableFractionId,
    // swing wt
    swingWtPrefixId: swingWtData?.swingWtPrefixId,
    swingWtPrefixName: swingWtData?.swingWtPrefixName || '',
    swingWtSuffixId: swingWtData?.swingWtSuffixId,
    swingWtSuffixName: swingWtData?.swingWtSuffixName || '',
    // shaft length
    shaftLengthDigit: shaftLengthData?.digit || '',
    shaftLengthDigitId: shaftLengthData?.joinTableDigitId,
    shaftLengthDigitId1: Number(shaftLengthData?.sourceTableDigitId),
    shaftLengthFraction: shaftLengthData?.fraction || '',
    shaftLengthFractionId: Number(shaftLengthData?.joinTableFractionId),
    shaftLengthFractionId1: Number(shaftLengthData?.sourceTableFractionId),
  };
};

interface IBuildPutterMetadataProps {
  player: IActivePlayer | null;
}

export const buildPutterMetadata = ({ player }: IBuildPutterMetadataProps) => ({
  // player props
  playerId: Number(player?.playerId),
  playerName: player?.fullName || '',
  // tour props
  tourId: Number(player?.defaultTourId),
  tourName: player?.defaultTourName || '',
  // edit props
  editSource: isIOSWebView ? 'iOS' : 'web',
});
